import React, { useEffect, useMemo } from "react";
import { Button, Form, Input, Space, Typography, Drawer } from "antd";
import { formatCurrency, isNilTextOrEmpty } from "@salespark/toolkit";
import { getCurrentPrice } from "utils/helper";

const { Text, Title } = Typography;

const SalesDrawer = ({ visible, onClose, onConfirm, wareData, selectedDate, initialValues }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!visible || !wareData) return;
    let unit = "sale_unit";

    if (isNilTextOrEmpty(wareData.sale_unit)) {
      unit = "movement_unit";
    }
    form.setFieldsValue({
      type: initialValues?.type || "sale",
      unit: unit,
      quantity: initialValues?.original_quantity || 1,
      notes: initialValues?.notes || "",
    });

    return () => form.resetFields();
  }, [visible, wareData, initialValues, form]);

  const type = Form.useWatch("type", form);
  const unit = Form.useWatch("unit", form);
  const quantity = Form.useWatch("quantity", form) || 0;
  const selectedPaymentMethod = Form.useWatch("payment_method", form);

  const unitPrice = useMemo(() => {
    if (!wareData || type !== "sale") return 0;

    const pricesByUnit = wareData.sell_price?.filter((p) => p.unit === unit) || [];

    if (unit === "sale_unit" && pricesByUnit.length === 0) {
      const fallback = wareData.sell_price?.filter((p) => p.unit === "movement_unit");
      return getCurrentPrice(fallback, selectedDate?.toDate());
    }

    return getCurrentPrice(pricesByUnit, selectedDate?.toDate());
  }, [wareData, selectedDate, unit, type]);

  const previewTotal = useMemo(() => {
    if (type !== "sale") return 0;
    return unitPrice * quantity;
  }, [unitPrice, quantity, type]);

  const handleOk = async () => {
    const values = await form.validateFields();
    if (values.type === "consumption") {
      values.unit = "sale_unit";
    }
    if (isNilTextOrEmpty(values.unit)) {
      return;
    }

    values.quantity = parseInt(values.quantity);
    onConfirm(values);
    form.resetFields();
    onClose();
  };

  if (!wareData) return null;

  return (
    <Drawer
      title="Detalhes da Venda / Consumo"
      open={visible}
      onOk={handleOk}
      onCancel={onClose}
      onClose={onClose}
      okText="Confirmar"
      cancelText="Cancelar"
      size={720}
      destroyOnHidden
      footer={
        <div
          style={{
            textAlign: "right",
          }}
        >
          <Button size="large" onClick={onClose} style={{ marginRight: 8 }}>
            Cancelar
          </Button>
          <Button size="large" type="primary" onClick={handleOk}>
            Confirmar
          </Button>
        </div>
      }
    >
      <Form form={form} layout="horizontal" className="compact-form">
        <Form.Item label="Artigo">
          <Text strong style={{ fontSize: 16 }}>
            {wareData.description}
          </Text>
        </Form.Item>

        {/* Type selector */}
        <Form.Item label="Tipo" name="type">
          <Space>
            <Button size="large" type={type === "sale" ? "primary" : "default"} onClick={() => form.setFieldsValue({ type: "sale", unit: "sale_unit" })}>
              Venda
            </Button>
            <Button
              size="large"
              type={type === "consumption" ? "primary" : "default"}
              onClick={() => form.setFieldsValue({ type: "consumption", unit: "sale_unit" })}
            >
              Consumo
            </Button>
          </Space>
        </Form.Item>

        {/* Unit selector (sale only) */}
        {type === "sale" && wareData.sale_unit && (
          <Form.Item label="Unidade" name="unit">
            <Space>
              <Button size="large" type={unit === "sale_unit" ? "primary" : "default"} onClick={() => form.setFieldsValue({ unit: "sale_unit" })}>
                {wareData.sale_unit} <small>(Un. Venda)</small>
              </Button>
              <Button size="large" type={unit === "movement_unit" ? "primary" : "default"} onClick={() => form.setFieldsValue({ unit: "movement_unit" })}>
                {wareData.movement_unit} <small>(Un. Movimento)</small>
              </Button>
            </Space>
          </Form.Item>
        )}
        {/* Unit selector (sale only) */}
        {type === "sale" && !wareData.sale_unit && (
          <Form.Item label="Unidade" name="unit">
            <Space>
              <Button size="large" type={unit === "movement_unit" ? "primary" : "default"} onClick={() => form.setFieldsValue({ unit: "movement_unit" })}>
                {wareData.movement_unit} <small>(Un. Movimento)</small>
              </Button>
            </Space>
          </Form.Item>
        )}

        {/* Unit price */}
        {type === "sale" && (
          <Form.Item label="Preço Unitário">
            <Title level={5} className="mt-0">
              {formatCurrency(unitPrice)}
            </Title>
          </Form.Item>
        )}

        {/* Payment method price */}
        {type === "sale" && (
          <Form.Item label="Método de Pagamento" name="payment_method" rules={[{ required: true, message: "Selecione um método de pagamento" }]}>
            <Space>
              {/* {paymentMethods.map((p) => (
                <Button
                  size="large"
                  type={selectedPaymentMethod === p._id ? "primary" : "default"}
                  onClick={() => form.setFieldsValue({ payment_method: p._id })}
                >
                  {p.name}
                </Button>
              ))} */}
              <Button
                size="large"
                type={selectedPaymentMethod === "payment_method_money" ? "primary" : "default"}
                onClick={() => form.setFieldsValue({ payment_method: "payment_method_money" })}
              >
                Dinheiro
              </Button>
              <Button
                size="large"
                type={selectedPaymentMethod === "payment_method_card" ? "primary" : "default"}
                onClick={() => form.setFieldsValue({ payment_method: "payment_method_card" })}
              >
                Cartão
              </Button>
            </Space>
          </Form.Item>
        )}

        {/* Quantity */}
        <Form.Item label="Quantidade" name="quantity" rules={[{ required: true, message: "Insira a quantidade" }]}>
          <Space.Compact>
            <Button size="large" onClick={() => form.setFieldsValue({ quantity: Math.max(0, quantity - 1) })}>
              −
            </Button>
            <Input
              min={1}
              value={quantity}
              onChange={(value) => form.setFieldsValue({ quantity: parseInt(value) ?? 1 })}
              style={{ width: 80, textAlign: "center" }}
              controls={false}
            />
            <Button size="large" onClick={() => form.setFieldsValue({ quantity: parseInt(quantity) + 1 })}>
              +
            </Button>
          </Space.Compact>
        </Form.Item>

        {/* Notes */}
        <Form.Item label="Notas" name="notes">
          <Input.TextArea rows={3} placeholder="Observações..." />
        </Form.Item>

        {/* Total */}
        {type === "sale" && (
          <div
            style={{
              textAlign: "right",
              marginTop: 16,
              paddingTop: 12,
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Text strong style={{ fontSize: 18 }}>
              Total: {formatCurrency(previewTotal)}
            </Text>
          </div>
        )}
      </Form>
    </Drawer>
  );
};

export default SalesDrawer;
